/* eslint-disable no-bitwise */
import { keccak256, toBytes } from 'viem'
import { normalize } from 'viem/ens'

export function concatenateUint8Arrays(arrays: Uint8Array[]): Uint8Array {
  let totalLength = 0

  for (let i = 0; i < arrays.length; i++) {
    totalLength += arrays[i].length
  }

  const concatenatedArray = new Uint8Array(totalLength)
  let offset = 0

  for (let i = 0; i < arrays.length; i++) {
    concatenatedArray.set(arrays[i], offset)
    offset += arrays[i].length
  }

  return concatenatedArray
}

export function bitwiseIsZero(hex1: string, hex2: string): boolean {
  // Parse the hexadecimal strings into BigInt
  const num1 = BigInt(hex1)
  const num2 = BigInt(hex2)

  // Perform the bitwise AND operation
  return (num1 & num2) === 0n
}

function bytesToHex(bytes: Uint8Array): string {
  return `0x${Array.from(bytes)
    .map(byte => byte.toString(16).padStart(2, '0'))
    .join('')}`
}

function generateRandomString(length: number): string {
  const characters = '0123456789'
  let result = ''

  // Use better random generator if available
  for (let i = 0; i < length; i++) {
    const randomIndex = Math.floor(Math.random() * characters.length)
    result += characters.charAt(randomIndex)
  }

  return result
}

const mineNonce = async (addr: string, handle: string, difficulty: string): Promise<string> => {
  // Encode strings
  const addrBytes = toBytes(addr)
  const handleBytes = toBytes(handle)

  // Start nonce at 0
  let nonce = 0
  let hash = '0xFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFF'
  const nonceBytes = new Uint8Array(4)

  while (!bitwiseIsZero(hash, difficulty)) {
    // Set nonce bytes
    nonceBytes[0] = (nonce >> 24) & 0xff
    nonceBytes[1] = (nonce >> 16) & 0xff
    nonceBytes[2] = (nonce >> 8) & 0xff
    nonceBytes[3] = nonce & 0xff
    // Hash
    hash = keccak256(concatenateUint8Arrays([addrBytes, handleBytes, nonceBytes]))
    // Increment nonce
    nonce++
  }
  return bytesToHex(nonceBytes)
}

/* -------------------------------------------------------------------------- */
/*                             Exported Functions                             */
/* -------------------------------------------------------------------------- */
const claimFreeLogic = async (addr: string, handle: string, difficulty: string) => {
  // Normalize handle
  const normalizedHandle = normalize(handle)

  // Generate random 4 digit code
  const code = generateRandomString(4)
  const fullHandle = normalizedHandle + code

  // Mine the nonce
  const nonceHex = await mineNonce(addr, fullHandle, difficulty)

  return { fullHandle, nonceHex, code }
}

export default claimFreeLogic
